/**
 * MetricsDashboard Component
 *
 * Real-time dashboard displaying NZ grid metrics from the backend.
 * No fake data - all metrics are derived from actual grid configuration
 * and solver results.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  dashboardApi,
  DashboardMetrics,
  GenerationMixItem,
} from '../services/api';
import { useWitsPrices } from '../hooks/useApi';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

// =============================================================================
// Sub-Components
// =============================================================================

interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
  status?: 'good' | 'warning' | 'critical';
  size?: 'compact' | 'full';
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  unit,
  trend,
  status = 'good',
  size = 'compact',
}) => {
  const isFull = size === 'full';
  const statusColors = {
    good: 'border-green-500/30 bg-green-500/5',
    warning: 'border-yellow-500/30 bg-yellow-500/5',
    critical: 'border-red-500/30 bg-red-500/5',
  };

  const trendIcons = {
    up: '\u2191',
    down: '\u2193',
    stable: '\u2192',
  };

  return (
    <div
      className={`rounded-lg border ${isFull ? 'p-5' : 'p-3'} ${
        statusColors[status]
      } transition-all hover:scale-[1.02]`}
    >
      <div
        className={`uppercase tracking-wide mb-1 ${
          isFull ? 'text-sm text-gray-200' : 'text-xs text-gray-400'
        }`}
      >
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`${isFull ? 'text-4xl' : 'text-2xl'} font-bold text-white`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </span>
        {unit && (
          <span className={`${isFull ? 'text-base' : 'text-sm'} text-gray-400`}>
            {unit}
          </span>
        )}
        {trend && (
          <span
            className={`ml-2 ${isFull ? 'text-base' : 'text-sm'} ${
              trend === 'up'
                ? 'text-green-400'
                : trend === 'down'
                ? 'text-red-400'
                : 'text-gray-400'
            }`}
          >
            {trendIcons[trend]}
          </span>
        )}
      </div>
    </div>
  );
};

const formatAge = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 1) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
};

// Generation Mix Donut Chart
interface GenerationMixChartProps {
  mix: GenerationMixItem[];
  size?: number;
  strokeWidth?: number;
  legendClassName?: string;
}

const GenerationMixChart: React.FC<GenerationMixChartProps> = ({
  mix,
  size = 120,
  strokeWidth = 20,
  legendClassName = 'text-xs',
}) => {
  // Calculate SVG arc paths for donut chart
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let cumulativePercentage = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#374151"
          strokeWidth={strokeWidth}
        />
        {mix.map((item, index) => {
          const strokeDasharray = `${
            (item.percentage / 100) * circumference
          } ${circumference}`;
          const strokeDashoffset = -(cumulativePercentage / 100) * circumference;
          cumulativePercentage += item.percentage;

          return (
            <circle
              key={item.fuel_type}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className={`flex flex-col gap-1 ${legendClassName}`}>
        {mix.slice(0, 5).map((item) => (
          <div key={item.fuel_type} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-gray-300 capitalize">{item.fuel_type}</span>
            <span className="text-gray-500 ml-auto">{item.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Island Balance Bar
interface IslandBalanceProps {
  islands: Array<{
    name: string;
    capacity_mw: number;
    load_mw: number;
    stress_level: number;
  }>;
  hvdc_flow_mw: number;
  hvdc_direction: 'north' | 'south';
  size?: 'compact' | 'full';
}

const IslandBalance: React.FC<IslandBalanceProps> = ({
  islands,
  hvdc_flow_mw,
  hvdc_direction,
  size = 'compact',
}) => {
  const isFull = size === 'full';
  const labelClass = isFull ? 'text-sm' : 'text-xs';
  const barHeight = isFull ? 'h-3' : 'h-2';

  const getStressColor = (stress: number) => {
    if (stress < 0.6) return 'bg-green-500';
    if (stress < 0.8) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-3">
      {islands.map((island) => (
        <div key={island.name} className="space-y-1">
          <div className={`flex justify-between ${labelClass}`}>
            <span className="text-gray-400">{island.name}</span>
            <span className="text-gray-300">
              {island.load_mw.toFixed(0)} / {island.capacity_mw.toFixed(0)} MW
            </span>
          </div>
          <div className={`${barHeight} bg-gray-700 rounded-full overflow-hidden`}>
            <div
              className={`h-full ${getStressColor(island.stress_level)} transition-all duration-500`}
              style={{ width: `${island.stress_level * 100}%` }}
            />
          </div>
        </div>
      ))}

      {/* HVDC Flow */}
      <div className="mt-4 pt-3 border-t border-gray-700">
        <div className={`flex items-center justify-between ${labelClass}`}>
          <span className="text-gray-400">HVDC Transfer</span>
          <div className="flex items-center gap-2">
            <span
              className={`${
                hvdc_direction === 'north' ? 'text-blue-400' : 'text-green-400'
              }`}
            >
              {hvdc_direction === 'north' ? '\u2191 North' : '\u2193 South'}
            </span>
            <span className="text-white font-medium">{hvdc_flow_mw} MW</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Solver Status Panel
interface SolverStatusProps {
  solver: {
    last_solve_time_ms: number;
    last_iterations: number;
    last_converged: boolean;
    avg_solve_time_ms: number;
    total_solves: number;
    success_rate: number;
  };
  size?: 'compact' | 'full';
}

const SolverStatus: React.FC<SolverStatusProps> = ({ solver, size = 'compact' }) => {
  const isFull = size === 'full';

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-center">
        <div className={`${isFull ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
          {solver.last_solve_time_ms.toFixed(1)}
        </div>
        <div className={`${isFull ? 'text-sm' : 'text-xs'} text-gray-400`}>Last (ms)</div>
      </div>
      <div className="text-center">
        <div className={`${isFull ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
          {solver.avg_solve_time_ms.toFixed(1)}
        </div>
        <div className={`${isFull ? 'text-sm' : 'text-xs'} text-gray-400`}>Avg (ms)</div>
      </div>
      <div className="text-center">
        <div
          className={`${isFull ? 'text-3xl' : 'text-2xl'} font-bold ${
            solver.success_rate >= 95
              ? 'text-green-400'
              : solver.success_rate >= 80
              ? 'text-yellow-400'
              : 'text-red-400'
          }`}
        >
          {solver.success_rate}%
        </div>
        <div className={`${isFull ? 'text-sm' : 'text-xs'} text-gray-400`}>Success</div>
      </div>
    </div>
  );
};

// System Health Bar
interface HealthBarProps {
  label: string;
  value: number;
  max: number;
  unit: string;
  thresholds?: { warning: number; critical: number };
  size?: 'compact' | 'full';
}

const HealthBar: React.FC<HealthBarProps> = ({
  label,
  value,
  max,
  unit,
  thresholds = { warning: 70, critical: 90 },
  size = 'compact',
}) => {
  const isFull = size === 'full';
  const percentage = (value / max) * 100;
  const getColor = () => {
    if (percentage >= thresholds.critical) return 'bg-red-500';
    if (percentage >= thresholds.warning) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-1">
      <div className={`flex justify-between ${isFull ? 'text-sm' : 'text-xs'}`}>
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-300">
          {value.toFixed(1)} {unit}
        </span>
      </div>
      <div className={`${isFull ? 'h-2.5' : 'h-1.5'} bg-gray-700 rounded-full overflow-hidden`}>
        <div
          className={`h-full ${getColor()} transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

const stressColor = (stress: number) => {
  if (stress >= 0.85) return 'bg-red-500/40 text-red-200';
  if (stress >= 0.7) return 'bg-orange-500/35 text-orange-200';
  if (stress >= 0.4) return 'bg-yellow-500/25 text-yellow-200';
  return 'bg-green-500/20 text-green-200';
};

const RegionalStressTable: React.FC<{
  regions: DashboardMetrics['regional_stress'];
  limit?: number;
}> = ({ regions, limit = 10 }) => {
  const topRegions = useMemo(() => {
    return [...(regions || [])]
      .sort((a, b) => b.stress_level - a.stress_level)
      .slice(0, limit);
  }, [regions, limit]);

  if (!topRegions.length) {
    return <div className="text-sm text-gray-400">No regional stress data available.</div>;
  }

  return (
    <div className="space-y-2">
      {topRegions.map((r) => {
        const pct = Math.round(r.stress_level * 100);
        const barClass = stressColor(r.stress_level).split(' ')[0];
        return (
          <div key={r.region_code} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-6">
              <div className="text-sm text-white font-medium truncate">{r.region_name}</div>
              <div className="text-xs text-gray-400">
                {Math.round(r.load_mw).toLocaleString()} MW load · {Math.round(r.capacity_mw).toLocaleString()} MW support
              </div>
            </div>
            <div className="col-span-4">
              <div className="h-2 w-full bg-gray-700 rounded overflow-hidden">
                <div
                  className={`h-full ${barClass}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
            <div className="col-span-2 flex justify-end">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${stressColor(r.stress_level)}`}>
                {pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const WitsPriceSummaryCard: React.FC = () => {
  const preferredNodes = ['BEN2201', 'HAY2201', 'OTA2201', 'ISL2201'] as const;
  const { data, isLoading, error } = useWitsPrices();

  const summary = useMemo(() => {
    const prices = data?.prices || [];
    if (!prices.length) return null;

    const byCode = new Map<string, { node_code: string; price_nzd_mwh: number; timestamp: string }>();
    for (const p of prices) {
      if (!p?.node_code) continue;
      byCode.set(p.node_code, p);
    }

    const selected = preferredNodes.filter((n) => byCode.has(n));
    const fallback = selected.length ? selected : Array.from(byCode.keys()).slice(0, 4);
    const picked = fallback
      .map((code) => byCode.get(code))
      .filter(Boolean) as Array<{ node_code: string; price_nzd_mwh: number; timestamp: string }>;

    const asOfMs = picked.reduce((latest, p) => {
      const ts = new Date(p.timestamp).getTime();
      return ts > latest ? ts : latest;
    }, 0);

    return { picked, asOfMs: asOfMs || null, count: prices.length };
  }, [data]);

  return (
    <div className="bg-gray-800/50 rounded-lg p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium text-gray-300">WITS Prices (RTD)</h3>
          <DataBadge tier="LIVE" />
        </div>
        {summary?.asOfMs && (
          <span className="text-xs text-gray-500">
            {summary.count.toLocaleString()} nodes · {new Date(summary.asOfMs).toLocaleTimeString()}
          </span>
        )}
      </div>
      <DataProvenanceRow
        tier="LIVE"
        source={data?.provenance?.source || data?.source || 'WITS'}
        asOf={data?.provenance?.as_of || data?.timestamp}
        generatedAt={data?.provenance?.generated_at}
        refresh="poll ~60s"
        inputs={data?.provenance?.inputs || null}
        warnings={data?.provenance?.warnings || null}
        className="mt-2"
        compact
      />
      {isLoading && !summary && (
        <div className="text-sm text-gray-400 animate-pulse">Loading WITS prices…</div>
      )}
      {!isLoading && error && !summary && (
        <div className="text-sm text-red-300">
          WITS unavailable: {(error as Error).message}
        </div>
      )}
      {summary && (
        <div className="grid grid-cols-2 gap-3">
          {summary.picked.map((p) => (
            <div key={p.node_code} className="rounded border border-gray-700 p-3">
              <div className="text-xs text-gray-400">{p.node_code}</div>
              <div className="text-2xl font-bold text-white">
                ${p.price_nzd_mwh.toFixed(1)}
                <span className="text-sm text-gray-400 font-normal">/MWh</span>
              </div>
              <div className="text-xs text-gray-500">
                {new Date(p.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      )}
      {!isLoading && !error && !summary && <div className="text-sm text-gray-400">No WITS prices returned.</div>}
    </div>
  );
};

// =============================================================================
// Main Dashboard Component
// =============================================================================

interface MetricsDashboardProps {
  loadFactor?: number;
  refreshInterval?: number; // ms
  variant?: 'compact' | 'full';
}

const MetricsDashboard: React.FC<MetricsDashboardProps> = ({
  loadFactor = 0.7,
  refreshInterval = 5000,
  variant = 'compact',
}) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchAt, setLastFetchAt] = useState<Date | null>(null);
  const [lastDataAt, setLastDataAt] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const isFull = variant === 'full';
  const hasMetricsRef = useRef(false);

  const fetchMetrics = useCallback(async () => {
    if (hasMetricsRef.current) setRefreshing(true);
    try {
      const data = await dashboardApi.getMetrics(loadFactor);
      setMetrics(data);
      const fetchedAt = new Date();
      setLastFetchAt(fetchedAt);
      const dataAt = data?.timestamp ? new Date(data.timestamp) : null;
      setLastDataAt(dataAt && Number.isFinite(dataAt.getTime()) ? dataAt : null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadFactor]);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchMetrics, refreshInterval]);

  useEffect(() => {
    hasMetricsRef.current = Boolean(metrics);
  }, [metrics]);

  if (loading && !metrics) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="animate-pulse">Loading dashboard metrics...</div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="p-4 text-center text-red-400">
        <div>Error: {error}</div>
        <button
          onClick={fetchMetrics}
          className="mt-2 px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) return null;

  const { grid, solver, health } = metrics;
  const now = Date.now();
  const dataAgeSeconds = lastDataAt ? Math.max(0, (now - lastDataAt.getTime()) / 1000) : null;
  const fetchAgeSeconds = lastFetchAt ? Math.max(0, (now - lastFetchAt.getTime()) / 1000) : null;
  const statusLabel =
    dataAgeSeconds === null
      ? 'Unknown'
      : dataAgeSeconds < (refreshInterval / 1000) * 2
      ? 'Fresh'
      : dataAgeSeconds < 120
      ? 'Delayed'
      : 'Stale';
  const statusColor =
    statusLabel === 'Fresh'
      ? 'text-green-300'
      : statusLabel === 'Delayed'
      ? 'text-amber-300'
      : statusLabel === 'Stale'
      ? 'text-red-300'
      : 'text-gray-400';

  return (
    <div className={`${isFull ? 'space-y-6' : 'space-y-4'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={`${isFull ? 'text-2xl' : 'text-lg'} font-semibold text-white`}>
            NZ Grid Dashboard
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
            <span className={statusColor}>{statusLabel}</span>
            {fetchAgeSeconds !== null && <span>polled {formatAge(fetchAgeSeconds)} ago</span>}
          </div>
          <DataProvenanceRow
            tier={metrics.provenance?.tier || 'MODELLED'}
            source={metrics.provenance?.source || 'dashboard/metrics'}
            asOf={metrics.provenance?.as_of || metrics.timestamp}
            generatedAt={metrics.provenance?.generated_at}
            refresh={`poll ${Math.round(refreshInterval / 1000)}s`}
            inputs={metrics.provenance?.inputs || { load_factor: loadFactor }}
            warnings={metrics.provenance?.warnings || null}
            className="mt-2"
            compact={!isFull}
          />
        </div>

        <div className="flex items-center gap-2">
          {error && (
            <span className="text-xs text-red-300 max-w-[420px] truncate">
              {error}
            </span>
          )}
          <button
            onClick={fetchMetrics}
            disabled={refreshing}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              refreshing
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isFull ? (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 xl:col-span-8 2xl:col-span-9 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                label="Total Capacity"
                value={Math.round(grid.total_capacity_mw)}
                unit="MW"
                status="good"
                size="full"
              />
              <MetricCard
                label="Total Load"
                value={Math.round(grid.total_load_mw)}
                unit="MW"
                status={
                  grid.total_load_mw / grid.total_capacity_mw > 0.9
                    ? 'critical'
                    : grid.total_load_mw / grid.total_capacity_mw > 0.7
                    ? 'warning'
                    : 'good'
                }
                size="full"
              />
              <MetricCard
                label="Success Rate"
                value={Math.round(solver.success_rate)}
                unit="%"
                status={solver.success_rate < 90 ? 'warning' : 'good'}
                size="full"
              />
              <MetricCard
                label="HVDC Flow"
                value={Math.round(grid.hvdc_flow_mw)}
                unit="MW"
                trend={grid.hvdc_direction === 'north' ? 'up' : 'down'}
                status="good"
                size="full"
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-gray-800/50 rounded-lg p-5 min-h-[260px]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-gray-300">Generation Mix</h3>
                  <DataBadge tier="MODELLED" />
                </div>
                <GenerationMixChart
                  mix={grid.generation_mix}
                  size={220}
                  strokeWidth={30}
                  legendClassName="text-base"
                />
              </div>

              <div className="bg-gray-800/50 rounded-lg p-5 min-h-[260px]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-gray-300">Island Balance</h3>
                  <DataBadge tier="MODELLED" />
                </div>
                <IslandBalance
                  islands={grid.islands}
                  hvdc_flow_mw={grid.hvdc_flow_mw}
                  hvdc_direction={grid.hvdc_direction}
                  size="full"
                />
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-medium text-gray-300">Regional Stress (Top)</h3>
                  <DataBadge tier="MODELLED" />
                </div>
                <span className="text-xs text-gray-500">derived from loads + generation + line ratings</span>
              </div>
              <RegionalStressTable regions={metrics.regional_stress} />
            </div>
          </div>

          <div className="col-span-12 xl:col-span-4 2xl:col-span-3 space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-medium text-gray-300">Solver Performance</h3>
                <DataBadge tier="SYSTEM" />
              </div>
              <SolverStatus solver={solver} size="full" />
              <div className="mt-3 pt-3 border-t border-gray-700 flex items-center justify-between text-sm">
                <span className="text-gray-400">Total Solves</span>
                <span className="text-white">{solver.total_solves}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-400">Last Converged</span>
                <span className={solver.last_converged ? 'text-green-400' : 'text-red-400'}>
                  {solver.last_converged ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-medium text-gray-300">System Health</h3>
                <DataBadge tier="SYSTEM" />
              </div>
              <div className="space-y-2">
                <HealthBar label="Memory" value={health.memory_percent} max={100} unit="%" size="full" />
                <HealthBar label="CPU" value={health.cpu_percent} max={100} unit="%" size="full" />
              </div>
              <div className="mt-3 pt-3 border-t border-gray-700 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Rust Solver</span>
                  <span className={health.rust_solver_available ? 'text-green-400' : 'text-red-400'}>
                    {health.rust_solver_available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Uptime</span>
                  <span className="text-white">
                    {Math.floor(health.api_uptime_seconds / 60)}m {Math.floor(health.api_uptime_seconds % 60)}s
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Memory</span>
                  <span className="text-white">{health.memory_usage_mb} MB</span>
                </div>
              </div>
            </div>

            <WitsPriceSummaryCard />
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="Total Capacity"
              value={Math.round(grid.total_capacity_mw)}
              unit="MW"
              status="good"
              size="compact"
            />
            <MetricCard
              label="Total Load"
              value={Math.round(grid.total_load_mw)}
              unit="MW"
              status={
                grid.total_load_mw / grid.total_capacity_mw > 0.9
                  ? 'critical'
                  : grid.total_load_mw / grid.total_capacity_mw > 0.7
                  ? 'warning'
                  : 'good'
              }
              size="compact"
            />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3 min-h-[220px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Generation Mix</h3>
              <DataBadge tier="MODELLED" />
            </div>
            <GenerationMixChart mix={grid.generation_mix} size={120} strokeWidth={20} legendClassName="text-xs" />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Island Balance</h3>
              <DataBadge tier="MODELLED" />
            </div>
            <IslandBalance
              islands={grid.islands}
              hvdc_flow_mw={grid.hvdc_flow_mw}
              hvdc_direction={grid.hvdc_direction}
              size="compact"
            />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Regional Stress</h3>
              <DataBadge tier="MODELLED" />
            </div>
            <RegionalStressTable regions={metrics.regional_stress} limit={6} />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Solver Performance</h3>
              <DataBadge tier="SYSTEM" />
            </div>
            <SolverStatus solver={solver} size="compact" />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">System Health</h3>
              <DataBadge tier="SYSTEM" />
            </div>
            <div className="space-y-2">
              <HealthBar label="Memory" value={health.memory_percent} max={100} unit="%" size="compact" />
              <HealthBar label="CPU" value={health.cpu_percent} max={100} unit="%" size="compact" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetricsDashboard;
